import React, { useEffect, useState } from "react";
import {
  CalendarDays,
  Clock3,
  CheckCircle2,
  RefreshCcw,
  User,
  Search,
  Bell,
  Stethoscope,
  XCircle,
  Phone,
} from "lucide-react";

import API from "../api";
import Navbar from "./Navbar";

const DoctorAppointmentDashboard = () => {

  // =========================================
  // STATES
  // =========================================

  const [appointments, setAppointments] =
    useState([]);

  const [
    filteredAppointments,
    setFilteredAppointments,
  ] = useState([]);

  const [
    confirmedTodayCount,
    setConfirmedTodayCount,
  ] = useState(0);

  const [
    todayScheduleCount,
    setTodayScheduleCount,
  ] = useState(0);

  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");

  const [activeCard, setActiveCard] =
    useState(null);

  // =========================================
  // RESCHEDULE MODAL
  // =========================================

  const [
    rescheduleModal,
    setRescheduleModal,
  ] = useState(false);

  const [
    selectedAppointment,
    setSelectedAppointment,
  ] = useState(null);

  const [
    rescheduleData,
    setRescheduleData,
  ] = useState({
    date: "",
    time: "",
  });

  // =========================================
  // FETCH APPOINTMENTS
  // =========================================

  const fetchAppointments = async () => {

    try {

      setLoading(true);

      const response =
        await API.post(
          "/doctor/pending-appointments/"
        );

      setAppointments(
        response.data.appointments || []
      );

      setFilteredAppointments(
        response.data.appointments || []
      );

      setConfirmedTodayCount(
        response.data
          .confirmed_today_count || 0
      );

      setTodayScheduleCount(
        response.data
          .today_schedule_count || 0
      );

    } catch (error) {

      console.log(error);

    } finally {

      setLoading(false);

    }

  };

  // =========================================
  // INITIAL LOAD
  // =========================================

  useEffect(() => {

    fetchAppointments();

  }, []);

  // =========================================
  // SEARCH
  // =========================================

  useEffect(() => {

    const filtered =
      appointments.filter((item) =>
        item.patient_name
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          )
      );

    setFilteredAppointments(
      filtered
    );

  }, [search, appointments]);

  // =========================================
  // CONFIRM / REJECT
  // =========================================

  const handleAction = async (
    appointment,
    action
  ) => {

    try {

      setActiveCard(
        appointment.id
      );

      const response =
        await API.post(
          "/doctor/update-appointment-status",
          {
            appointment_id:
              appointment.id,

            status: action,
          }
        );

      if (
        response.data.status
      ) {

        alert(
          response.data.message
        );

        fetchAppointments();

      }

    } catch (error) {

      console.log(error);

      alert(
        "Something went wrong"
      );

    } finally {

      setActiveCard(null);

    }

  };

  // =========================================
  // RESCHEDULE
  // =========================================

  const confirmReschedule =
    async () => {

      try {

        if (
          !rescheduleData.date
        ) {

          alert(
            "Please select date"
          );

          return;

        }

        if (
          !rescheduleData.time
        ) {

          alert(
            "Please select time"
          );

          return;

        }

        setActiveCard(
          selectedAppointment.id
        );

        const response =
          await API.post(
            "/doctor/reschedule-appointment",
            {
              appointment_id:
                selectedAppointment.id,

              date:
                rescheduleData.date,

              time:
                rescheduleData.time,
            }
          );

        if (
          response.data.status
        ) {

          alert(
            response.data.message
          );

          setRescheduleModal(
            false
          );

          fetchAppointments();

        }

      } catch (error) {

        console.log(error);

        alert(
          "Failed to reschedule"
        );

      } finally {

        setActiveCard(null);

      }

    };

  // =========================================
  // REFRESH
  // =========================================

  const refreshData = () => {

    fetchAppointments();

  };

  // =========================================
  // UI
  // =========================================

  return (


    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pt-28  pb-6">
      <Navbar/>

      {/* ========================================= */}
      {/* HEADER */}
      {/* ========================================= */}
      <div className="pt-1 px-6 pb-10">

      <div className="bg-white/80 backdrop-blur-xl border border-white/20 rounded-[32px] p-6 shadow-xl mb-8">



        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

          {/* LEFT */}

          <div className="flex items-center gap-5">

            <div className="w-16 h-16 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-xl">

              <Stethoscope size={32} />

            </div>

            <div>

              <h1 className="text-4xl font-black text-slate-800">
                Appointment Requests
              </h1>

              <p className="text-slate-500 mt-1">
                Manage all patient appointment requests
              </p>

            </div>

          </div>

          {/* RIGHT */}

          <div className="flex items-center gap-4 flex-wrap">

            {/* SEARCH */}

            <div className="relative">

              <Search
                className="absolute left-4 top-4 text-slate-400"
                size={18}
              />

              <input
                type="text"
                placeholder="Search patient..."
                value={search}
                onChange={(e) =>
                  setSearch(
                    e.target.value
                  )
                }
                className="pl-11 pr-4 py-3 rounded-2xl border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-[280px]"
              />

            </div>

            {/* REFRESH */}

            <button
              onClick={
                refreshData
              }
              className="w-14 h-14 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-lg hover:scale-105 transition-all"
            >

              <RefreshCcw size={22} />

            </button>

          </div>

        </div>

      </div>

      {/* ========================================= */}
      {/* STATS */}
      {/* ========================================= */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

        {/* PENDING */}

        <div className="bg-white rounded-[30px] p-6 shadow-xl border border-slate-100">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-slate-500 text-sm font-medium">
                Pending Requests
              </p>

              <h2 className="text-5xl font-black text-slate-800 mt-2">
                {
                  appointments.length
                }
              </h2>

            </div>

            <div className="w-16 h-16 rounded-3xl bg-yellow-100 flex items-center justify-center text-yellow-600">

              <Bell size={30} />

            </div>

          </div>

        </div>

        {/* CONFIRMED */}

        <div className="bg-white rounded-[30px] p-6 shadow-xl border border-slate-100">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-slate-500 text-sm font-medium">
                Confirmed Today
              </p>

              <h2 className="text-5xl font-black text-slate-800 mt-2">
                {
                  confirmedTodayCount
                }
              </h2>

            </div>

            <div className="w-16 h-16 rounded-3xl bg-green-100 flex items-center justify-center text-green-600">

              <CheckCircle2 size={30} />

            </div>

          </div>

        </div>

        {/* TODAY */}

        <div className="bg-white rounded-[30px] p-6 shadow-xl border border-slate-100">

          <div className="flex items-center justify-between">

            <div>

              <p className="text-slate-500 text-sm font-medium">
                Today's Schedule
              </p>

              <h2 className="text-5xl font-black text-slate-800 mt-2">
                {
                  todayScheduleCount
                }
              </h2>

            </div>

            <div className="w-16 h-16 rounded-3xl bg-blue-100 flex items-center justify-center text-blue-600">

              <CalendarDays size={30} />

            </div>

          </div>

        </div>

      </div>

      {/* ========================================= */}
      {/* LOADING */}
      {/* ========================================= */}

      {loading ? (

        <div className="flex items-center justify-center h-[500px]">

          <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>

        </div>

      ) : (

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">

          {filteredAppointments.map(
            (appointment) => (

              <div
                key={
                  appointment.id
                }
                className="bg-white rounded-[36px] p-6 shadow-xl border border-slate-100 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              >

                {/* TOP */}

                <div className="flex items-start justify-between mb-6">

                  <div className="flex items-center gap-4">

                    {/* AVATAR */}

                    <div className="w-16 h-16 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white text-xl font-black shadow-xl">

                      {appointment.patient_name
                        ?.slice(0, 2)
                        ?.toUpperCase()}

                    </div>

                    <div>

                      <h2 className="text-2xl font-black text-slate-800">
                        {
                          appointment.patient_name
                        }
                      </h2>

                      <p className="text-slate-500">
                        Appointment Request
                      </p>

                    </div>

                  </div>

                  <div className="px-4 py-2 rounded-full bg-yellow-100 text-yellow-700 text-xs font-bold">

                    Pending

                  </div>

                </div>

                {/* INFO */}

                <div className="space-y-4 mb-6">

                  {/* DATE */}

                  <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between">

                    <div className="flex items-center gap-3 text-slate-600">

                      <CalendarDays size={18} />

                      <span>Date</span>

                    </div>

                    <span className="font-bold text-slate-800">
                      {
                        appointment.date
                      }
                    </span>

                  </div>

                  {/* TIME */}

                  <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between">

                    <div className="flex items-center gap-3 text-slate-600">

                      <Clock3 size={18} />

                      <span>Time</span>

                    </div>

                    <span className="font-bold text-slate-800">
                      {
                        appointment.time
                      }
                    </span>

                  </div>

                  {/* PATIENT */}

                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-5 border border-blue-100">

                    <div className="flex items-center gap-3 mb-3">

                      <User
                        size={18}
                        className="text-blue-600"
                      />

                      <span className="font-bold text-blue-700">
                        Patient Information
                      </span>

                    </div>

                    <div className="space-y-2 text-sm text-slate-700">

                      <p>
                        Name:
                        {" "}
                        {
                          appointment.patient_name
                        }
                      </p>

                      <div className="flex items-center gap-2">

                        <Phone
                          size={14}
                        />

                        <span>
                          {
                            appointment.patient_number
                          }
                        </span>

                      </div>

                    </div>

                  </div>

                </div>

                {/* BUTTONS */}

                <div className="grid grid-cols-3 gap-3">

                  {/* CONFIRM */}

                  <button
                    onClick={() =>
                      handleAction(
                        appointment,
                        "confirmed"
                      )
                    }
                    disabled={
                      activeCard ===
                      appointment.id
                    }
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-all"
                  >

                    {
                      activeCard ===
                        appointment.id
                        ? "..."
                        : "Confirm"
                    }

                  </button>

                  {/* CHANGE */}

                  <button
                    onClick={() => {

                      setSelectedAppointment(
                        appointment
                      );

                      setRescheduleModal(
                        true
                      );

                      setRescheduleData(
                        {
                          date:
                            appointment.date,

                          time:
                            appointment.time,
                        }
                      );

                    }}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-all"
                  >

                    Change

                  </button>

                  {/* REJECT */}

                  <button
                    onClick={() =>
                      handleAction(
                        appointment,
                        "rejected"
                      )
                    }
                    className="bg-gradient-to-r from-red-500 to-rose-500 text-white py-3 rounded-2xl font-bold shadow-lg hover:scale-105 transition-all"
                  >

                    Reject

                  </button>

                </div>

              </div>

            )
          )}

        </div>

      )}

      {/* ========================================= */}
      {/* RESCHEDULE MODAL */}
      {/* ========================================= */}

      {rescheduleModal && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">

          <div className="bg-white w-full max-w-lg rounded-[36px] shadow-2xl overflow-hidden">

            {/* HEADER */}

            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">

              <div className="flex items-center justify-between">

                <div>

                  <h2 className="text-3xl font-black">
                    Reschedule Appointment
                  </h2>

                  <p className="text-blue-100 mt-1">
                    Select new appointment slot
                  </p>

                </div>

                <button
                  onClick={() =>
                    setRescheduleModal(
                      false
                    )
                  }
                  className="w-12 h-12 rounded-2xl bg-white/20 hover:bg-white/30 flex items-center justify-center"
                >

                  <XCircle size={28} />

                </button>

              </div>

            </div>

            {/* BODY */}

            <div className="p-8">

              {/* DATE */}

              <div className="mb-6">

                <label className="block text-slate-700 font-bold mb-3">
                  Appointment Date
                </label>

                <input
                  type="date"
                  value={
                    rescheduleData.date
                  }
                  onChange={(e) =>
                    setRescheduleData(
                      {
                        ...rescheduleData,

                        date:
                          e.target.value,
                      }
                    )
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded-3xl p-5 outline-none text-lg font-semibold"
                />

              </div>

              {/* TIME */}

              <div>

                <label className="block text-slate-700 font-bold mb-3">
                  Appointment Time
                </label>

                <input
                  type="time"
                  value={
                    rescheduleData.time
                  }
                  onChange={(e) =>
                    setRescheduleData(
                      {
                        ...rescheduleData,

                        time:
                          e.target.value,
                      }
                    )
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded-3xl p-5 outline-none text-lg font-semibold"
                />

              </div>

              {/* BUTTONS */}

              <div className="grid grid-cols-2 gap-4 mt-10">

                <button
                  onClick={() =>
                    setRescheduleModal(
                      false
                    )
                  }
                  className="bg-slate-100 hover:bg-slate-200 text-slate-700 py-4 rounded-3xl font-black"
                >

                  Cancel

                </button>

                <button
                  onClick={
                    confirmReschedule
                  }
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-3xl font-black shadow-xl hover:scale-105 transition-all"
                >

                  Confirm Changes

                </button>

              </div>

            </div>

          </div>

        </div>

      )}
      </div>

    </div>

  );

};

export default DoctorAppointmentDashboard;